import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useCallback } from 'react';
import { z } from 'zod';

import { SignInSurface } from '@/features/auth';
import { useSession } from '@/lib/session/useSession';
import { telemetry } from '@/lib/telemetry';

/*
 * The /login route (finding 1.4). It mounts the REAL spec sign-in surface
 * (`SignInSurface`), which owns no credential form: its one Sign in act hands
 * the browser off to Identity's hosted login (ADR 0012), and Identity's
 * callback returns the browser here with a session. The surface's one generic,
 * cause-free failure text (never the server's `detail`) and its keyboard
 * contract run in production. The old `LoginPage` form (prefilled
 * `dana@example.com` / `password`, and an `ApiError.message` render that echoed
 * the Problem detail — the cause-specific leak) is gone.
 *
 * Topology (finding 3.B): sign-in is browser→Identity and the surface drives it
 * through the auth feature's mutation layer (`useSignIn`), which also does the
 * shared-machine cache isolation on success and invalidates `/me`. This route
 * does NOT call `api.auth.login` itself — it composes the surface and, once the
 * session names an individual (`/me` resolves), honours the `redirect` search
 * param. It also hands that `redirect` to the surface, which sends it on the
 * hand-off so Identity returns the browser to `/login?redirect=<path>` and the
 * deep link survives the round trip (spec §3.6). ADR 0009 teardown stays
 * deferred (still MSW-backed via
 * VITE_ENABLE_MOCK; this is a mount-the-real-surface swap, not a mock→live
 * swap).
 */

const searchSchema = z.object({
  redirect: z.string().optional(),
});

export const Route = createFileRoute('/login')({
  validateSearch: searchSchema,
  component: LoginPage,
});

function LoginPage() {
  const navigate = useNavigate();
  const { redirect } = Route.useSearch();
  const { session } = useSession();

  // Navigate only when the surface says the sign-in is COMPLETE — the
  // candidate set is known and any multi-match was resolved by a choice —
  // never on the session alone, which exists against the default profile as
  // soon as Identity returns the browser here (AC-a5578791). Attribute RUM to
  // the opaque user id only — never name/email/role (§13). Navigation lives
  // here (not in the surface) because WHICH page to land on is the route's.
  const onComplete = useCallback(() => {
    if (session) telemetry.setUser(session.id);
    void navigate({ to: redirect ?? '/' });
  }, [session, redirect, navigate]);

  return (
    <div className="flex min-h-dvh items-center justify-center p-6">
      <div className="w-full max-w-sm">
        <SignInSurface
          surface="kelly"
          redirect={redirect}
          onComplete={onComplete}
        />
      </div>
    </div>
  );
}
